import type { BevelParameterLabels } from './bevelGeometryImagesClient'
import type { FloorSheetDeposition } from './floorSheetCrcFields'
import type { FloorSheetFsTargetValues } from './floorSheetCrcFields'

/** Target Values settings draft (Miscellaneous fields live on the Pass Record tab). */
export type FloorSheetTargetValuesDraft = {
  unit: 'in' | 'mm'
  calculatedTopOfGroove: string
  fillAngleA: string
  shelfHeight: string
  landThickness: string
  offsetE: string
}

/** Miscellaneous Pass Record tab draft. */
export type FloorSheetMiscellaneousDraft = {
  unit: 'in' | 'mm'
  maxJointMisalignment: string
  afterRootPass: string
  afterCapsPass: string
}

type BevelParameterLabelMap = Partial<Record<string, string>>

/** Mirror server `stripWpsToleranceSuffix` — Target Values show nominal only. */
export function stripWpsToleranceSuffix(value: string): string {
  const t = value.trim()
  if (!t) return t
  const plusMinus = t.match(/^(.*?)(?:\s*[±]\s*.+)$/)
  if (plusMinus) return plusMinus[1]!.trim().replace(/,\s*$/, '')
  const plusMinusSlash = t.match(/^(.*?)(?:,?\s*\+\s*[^/]+\/\s*-\s*.+)$/)
  if (plusMinusSlash) return plusMinusSlash[1]!.trim().replace(/,\s*$/, '')
  return t
}

/** Match server `pickBevelLetterValue` — resolve geometry params from admin labels. */
export function pickBevelLetterValue(
  labels: BevelParameterLabelMap | null | undefined,
  values: BevelParameterLabelMap | null | undefined,
  letter: string,
  nameHints: RegExp
): string {
  const keys = Array.from({ length: 13 }, (_, i) => `parameter${i + 1}`)
  for (const key of keys) {
    const label = String(labels?.[key] ?? '').trim()
    const value = String(values?.[key] ?? '').trim()
    if (!value) continue
    if (letter) {
      const letterMatch = new RegExp(`\\(${letter}\\)|\\b${letter}\\b`, 'i')
      if (letterMatch.test(label) || nameHints.test(label)) return value
    } else if (nameHints.test(label)) {
      return value
    }
  }
  return ''
}

/** Resolve a bevel parameter by label hint only (no letter). */
export function pickBevelLabelValue(
  labels: BevelParameterLabelMap | null | undefined,
  values: BevelParameterLabelMap | null | undefined,
  nameHints: RegExp
): string {
  return pickBevelLetterValue(labels, values, '', nameHints)
}

function averageNumericClockValues(values: Record<string, string>): string {
  const nums: number[] = []
  for (const raw of Object.values(values ?? {})) {
    const t = String(raw ?? '').trim()
    if (!t) continue
    const n = Number(t)
    if (Number.isFinite(n)) nums.push(n)
  }
  if (nums.length === 0) return ''
  const mean = nums.reduce((a, b) => a + b, 0) / nums.length
  return String(Number(mean.toFixed(3)))
}

/** Collapse a legacy 4-clock map to one display value (avg, else first non-blank). */
export function scalarFromClockMap(values: Record<string, string>): string {
  const avg = averageNumericClockValues(values)
  if (avg) return avg
  for (const raw of Object.values(values ?? {})) {
    const t = String(raw ?? '').trim()
    if (t) return t
  }
  return ''
}

/** Broadcast a single target value to all clock slots for PDF avg / legacy storage. */
export function clockMapFromScalar(
  locations: string[],
  scalar: string
): Record<string, string> {
  const v = scalar.trim()
  return locations.reduce((acc, loc) => {
    acc[loc] = v
    return acc
  }, {} as Record<string, string>)
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

export function readBevelMaxJointMisalignment(
  sections: Record<string, unknown>,
  bevelIndex: number | null
): string {
  const bevel = asRecord(sections.bevel)
  const bevels = Array.isArray(bevel?.bevels) ? (bevel!.bevels as unknown[]) : []
  if (bevels.length === 0) return ''
  const idx =
    bevelIndex !== null && bevelIndex >= 0
      ? Math.min(bevelIndex, bevels.length - 1)
      : 0
  const active = asRecord(bevels[idx])
  return String(active?.maxJointMisalignment ?? '').trim()
}

export type BevelGeometryHints = Pick<
  FloorSheetTargetValuesDraft,
  'fillAngleA' | 'offsetE' | 'shelfHeight' | 'landThickness'
> & { maxJointMisalignment: string }

export function bevelGeometryHintsFromResolve(
  labels: BevelParameterLabels | null | undefined,
  values: BevelParameterLabels | null | undefined,
  sections: Record<string, unknown>,
  bevelIndex: number | null
): BevelGeometryHints {
  return {
    fillAngleA: stripWpsToleranceSuffix(
      pickBevelLetterValue(labels, values, 'A', /fill\s*angle/i)
    ),
    offsetE: stripWpsToleranceSuffix(pickBevelLetterValue(labels, values, 'E', /\boffset\b/i)),
    shelfHeight: stripWpsToleranceSuffix(
      pickBevelLabelValue(labels, values, /shelf\s*height/i)
    ),
    landThickness: stripWpsToleranceSuffix(
      pickBevelLabelValue(labels, values, /land\s*(thickness)?/i)
    ),
    maxJointMisalignment: readBevelMaxJointMisalignment(sections, bevelIndex)
  }
}

function avgFromByPass(deposition: FloorSheetDeposition, passName: string): string {
  const row = deposition.byPass[passName]
  if (!row) return ''
  return averageNumericClockValues(row)
}

function suggestAfterCapsPass(deposition: FloorSheetDeposition, passLabels: string[]): string {
  const capPasses = passLabels.filter((p) => /^cap\s*\d/i.test(p))
  for (let i = capPasses.length - 1; i >= 0; i -= 1) {
    const avg = avgFromByPass(deposition, capPasses[i]!)
    if (avg) return avg
  }
  const legacy = passLabels.filter((p) => /^cap/i.test(p))
  for (let i = legacy.length - 1; i >= 0; i -= 1) {
    const avg = avgFromByPass(deposition, legacy[i]!)
    if (avg) return avg
  }
  return ''
}

export function buildTargetValuesDraft(
  deposition: FloorSheetDeposition,
  _passLabels: string[],
  bevelHints: Pick<
    BevelGeometryHints,
    'fillAngleA' | 'offsetE' | 'shelfHeight' | 'landThickness'
  >
): FloorSheetTargetValuesDraft {
  return {
    unit: deposition.unit,
    calculatedTopOfGroove: deposition.calculatedTopOfGroove.trim(),
    fillAngleA: stripWpsToleranceSuffix(bevelHints.fillAngleA),
    shelfHeight: stripWpsToleranceSuffix(bevelHints.shelfHeight),
    landThickness: stripWpsToleranceSuffix(bevelHints.landThickness),
    offsetE: stripWpsToleranceSuffix(bevelHints.offsetE)
  }
}

export function buildMiscellaneousDraft(
  deposition: FloorSheetDeposition,
  passLabels: string[],
  maxJointMisalignment: string
): FloorSheetMiscellaneousDraft {
  const afterRootPass =
    scalarFromClockMap(deposition.afterRoot) || avgFromByPass(deposition, 'Root')
  const afterCapsPass =
    scalarFromClockMap(deposition.afterCaps) || suggestAfterCapsPass(deposition, passLabels)
  return {
    unit: deposition.unit,
    maxJointMisalignment: maxJointMisalignment.trim(),
    afterRootPass,
    afterCapsPass
  }
}

export function draftToFsTargetValues(
  draft: Pick<
    FloorSheetTargetValuesDraft,
    'fillAngleA' | 'offsetE' | 'shelfHeight' | 'landThickness'
  > & { maxJointMisalignment?: string },
  maxJointMisalignment = draft.maxJointMisalignment ?? ''
): FloorSheetFsTargetValues {
  return {
    maxJointMisalignment: maxJointMisalignment.trim(),
    fillAngleA: stripWpsToleranceSuffix(draft.fillAngleA.trim()),
    offsetE: stripWpsToleranceSuffix(draft.offsetE.trim()),
    shelfHeight: stripWpsToleranceSuffix(draft.shelfHeight.trim()),
    landThickness: stripWpsToleranceSuffix(draft.landThickness.trim())
  }
}

export function mergeTargetValuesDraftWithPersisted(
  draft: FloorSheetTargetValuesDraft,
  persisted: FloorSheetFsTargetValues | undefined
): FloorSheetTargetValuesDraft {
  if (!persisted) return draft
  // Live bevel wins when non-blank so WPS bevel edits refresh Target Values.
  return {
    ...draft,
    fillAngleA: stripWpsToleranceSuffix(draft.fillAngleA || persisted.fillAngleA),
    offsetE: stripWpsToleranceSuffix(draft.offsetE || persisted.offsetE),
    shelfHeight: stripWpsToleranceSuffix(draft.shelfHeight || persisted.shelfHeight),
    landThickness: stripWpsToleranceSuffix(draft.landThickness || persisted.landThickness)
  }
}

/** Settings save: update unit + calculated top only; after Root/Caps live on Misc tab. */
export function applyTargetValuesDraftToDeposition(
  deposition: FloorSheetDeposition,
  draft: FloorSheetTargetValuesDraft,
  _locations: string[]
): FloorSheetDeposition {
  return {
    ...deposition,
    unit: draft.unit,
    calculatedTopOfGroove: draft.calculatedTopOfGroove
  }
}

export function applyMiscellaneousDraftToWeldFields(
  deposition: FloorSheetDeposition,
  draft: FloorSheetMiscellaneousDraft,
  locations: string[],
  fsTargetValues: FloorSheetFsTargetValues
): { deposition: FloorSheetDeposition; fsTargetValues: FloorSheetFsTargetValues } {
  return {
    deposition: {
      ...deposition,
      unit: draft.unit,
      afterRoot: clockMapFromScalar(locations, draft.afterRootPass),
      afterCaps: clockMapFromScalar(locations, draft.afterCapsPass)
    },
    fsTargetValues: {
      ...fsTargetValues,
      maxJointMisalignment: draft.maxJointMisalignment.trim()
    }
  }
}
